import { v4 as uuid } from 'uuid'

import { isBillsId, type BillsEnvironment, type BillsTraffic } from './apBills'
import { posthogManager } from './manager'

// Screens whose correlation svc-partner-gateway forwards to backend services.
// Keep in sync with BillsAnalyticsExtensions in svc-partner-gateway.
export const apForwardedScreens = [
  'bills_list',
  'vendor_data_request',
  'ap_overview',
  'bill_create',
  'bill_edit',
  'bill_review_drawer',
  'bill_details_drawer',
  'bill_receiving_method',
  'bill_payment_review',
  'bill_funding_account_drawer',
  'bill_single_use_card_drawer',
  'vendors_listing',
  'vendor_creation',
  'vendor_editing',
  'vendor_details',
  'vendor_duplicate_review',
  'payments_listing',
  'cards_listing',
  'card_issuance',
  'card_page',
  'cards_reveal_drawer',
  'card_top_up_dialog',
  'card_close_dialog'
] as const

// Opaque intent IDs exist before any record does; the gateway forwards them so
// a browser attempt joins its committed backend outcome.
export const apIntentFields = [
  'submittedRevisionId',
  'formInstanceId',
  'saveOperationId',
  'issuanceIntentId',
  'cardholderDraftId',
  'topUpIntentId',
  'closureIntentId',
  'revealAttemptId',
  'mergeAttemptId',
  'uploadAttemptId',
  'uploadBatchId'
] as const
export type ApIntentField = (typeof apIntentFields)[number]
export type ApIntents = Partial<Record<ApIntentField, string>>

export const apErrorCodes = [
  'validation',
  'permission_denied',
  'state_conflict',
  'not_found',
  'duplicate',
  'missing_payment_preference',
  'funding_captured',
  'capture_in_flight',
  'retry_unavailable',
  'cancellation_unavailable',
  'clipboard_denied',
  'popup_blocked',
  'network',
  'timeout',
  'service_unavailable',
  'persistence_failed',
  'queue_failed',
  'provider_rejected',
  'unknown'
] as const
export type ApErrorCode = (typeof apErrorCodes)[number]

export type ApInputMethod = 'pointer' | 'keyboard' | 'drop'
export interface ApContext {
  screenId: string
  screenVisitId: string
  originScreenVisitId: string
  userId: string
  accountId: string
  partnerId: string
  environment: BillsEnvironment
  trafficClass: BillsTraffic
  sessionId?: string
}
export interface ApAttempt extends ApContext {
  actionAttemptId: string
  operationId: string
  inputMethod: ApInputMethod
  startedAt: number
  requestId?: string
  intents?: ApIntents
}
type Properties = Record<string, unknown>
type Enums = Record<string, readonly string[]>

export interface ApDomain {
  // Screen IDs this domain emits from. Forwarded screens need no listing.
  screens?: readonly string[]
  // Exact event name → space-separated allowlisted property names.
  events: Record<string, string>
  // Allowed string values, scoped to this domain's events.
  enums?: Enums
}

const sharedEnums: Enums = {
  errorCode: apErrorCodes,
  inputMethod: ['pointer', 'keyboard', 'drop']
}
const registry = new Map<string, { fields: Set<string>; enums: Enums }>()
const screens = new Set<string>(apForwardedScreens)

// Each feature registers its own dictionary, which is also the payload
// allowlist. Registering an existing event extends it.
export const registerApEvents = ({
  screens: domainScreens = [],
  events,
  enums = {}
}: ApDomain) => {
  for (const screen of domainScreens) screens.add(screen)
  for (const [name, fields] of Object.entries(events)) {
    const existing = registry.get(name)
    const merged: Enums = { ...sharedEnums, ...existing?.enums }
    for (const [field, values] of Object.entries(enums))
      merged[field] = Array.from(new Set([...(merged[field] ?? []), ...values]))
    registry.set(name, {
      fields: new Set([
        ...(existing?.fields ?? []),
        ...fields.split(' ').filter(Boolean)
      ]),
      enums: merged
    })
  }
}
export const apEventSpec = (name: string) => registry.get(name)
export const isApScreen = (screen: unknown): screen is string =>
  typeof screen === 'string' && screens.has(screen)
export const isApId = isBillsId

const attemptFields = [
  'actionAttemptId',
  'operationId',
  'requestId',
  'inputMethod'
]
// UUIDs, UUID lists, booleans, null, non-negative finite numbers and
// allowlisted enum strings. Everything else (text, emails, amounts typed as
// strings, error objects) is dropped.
export const safeApProperties = (
  fields: Iterable<string>,
  values: Properties,
  enums: Enums = sharedEnums
): Properties => {
  const result: Properties = {}
  for (const field of fields) {
    const value = values[field]
    const allowed = enums[field]
    if (allowed) {
      if (typeof value === 'string' && allowed.includes(value))
        result[field] = value
      else if (Array.isArray(value))
        result[field] = value.filter(
          v => typeof v === 'string' && allowed.includes(v)
        )
    } else if (/Ids$|IDs$/.test(field)) {
      if (Array.isArray(value)) result[field] = value.filter(isApId)
    } else if (/Id$|ID$/.test(field)) {
      if (isApId(value)) result[field] = value
    } else if (typeof value === 'boolean' || value === null)
      result[field] = value
    else if (typeof value === 'number' && Number.isFinite(value) && value >= 0)
      result[field] = value
  }
  return result
}

export const apSessionId = () => {
  try {
    const id = posthogManager.getClientInstance()?.get_session_id()
    return isApId(id) ? id : undefined
  } catch {
    return undefined
  }
}

const contextFields = (context: ApContext) => ({
  originSessionId: context.sessionId ?? apSessionId(),
  screenId: context.screenId,
  screenVisitId: context.screenVisitId,
  originScreenVisitId: context.originScreenVisitId,
  userId: context.userId,
  accountId: context.accountId,
  partnerId: context.partnerId,
  environment: context.environment,
  trafficClass: context.trafficClass
})

// The properties every AP event carries besides its own dictionary.
export const apCommonFields = [
  'originSessionId',
  'screenId',
  'screenVisitId',
  'originScreenVisitId',
  'userId',
  'accountId',
  'partnerId',
  'environment',
  'trafficClass',
  ...attemptFields,
  'eventId',
  'schemaVersion',
  'timestamp'
]

let inputMethod: ApInputMethod = 'pointer'
let listening = false
const listenForInputMethod = () => {
  if (listening || typeof document === 'undefined') return
  listening = true
  document.addEventListener(
    'pointerdown',
    () => (inputMethod = 'pointer'),
    true
  )
  document.addEventListener('keydown', () => (inputMethod = 'keyboard'), true)
  document.addEventListener('drop', () => (inputMethod = 'drop'), true)
}

const dedup = new Set<string>()
const errors = new WeakMap<object, { errorId: string; errorCode: string }>()

export const apErrorCode = (error: unknown): ApErrorCode => {
  if (!error || typeof error !== 'object') return 'unknown'
  const e = error as {
    name?: string
    statusCode?: number
    networkError?: { statusCode?: number }
    graphQLErrors?: { extensions?: { code?: unknown } }[]
  }
  if (e.name === 'AbortError' || e.name === 'TimeoutError') return 'timeout'
  if (e.name === 'NotAllowedError') return 'clipboard_denied'
  const status = e.statusCode ?? e.networkError?.statusCode
  if (status === 403 || status === 401) return 'permission_denied'
  if (status === 404) return 'not_found'
  if (status === 409) return 'state_conflict'
  if (status === 400 || status === 422) return 'validation'
  if (status && status >= 500) return 'service_unavailable'
  if (e.networkError) return 'network'
  const code = e.graphQLErrors?.[0]?.extensions?.code
  if (typeof code !== 'string') return 'unknown'
  const lower = code.toLowerCase()
  if ((apErrorCodes as readonly string[]).includes(lower))
    return lower as ApErrorCode
  if (lower === 'bad_user_input' || lower === 'invalid_argument')
    return 'validation'
  if (lower === 'forbidden' || lower === 'unauthenticated')
    return 'permission_denied'
  if (lower === 'already_exists') return 'duplicate'
  if (lower === 'failed_precondition') return 'state_conflict'
  if (lower === 'deadline_exceeded') return 'timeout'
  if (lower === 'unavailable') return 'service_unavailable'
  return 'unknown'
}

export const apAnalytics = {
  // Start a deliberate attempt: one per user action. A retry is a new attempt.
  attempt: (
    context: ApContext | undefined,
    intents?: ApIntents
  ): ApAttempt | undefined => {
    if (!context) return undefined
    listenForInputMethod()
    return {
      ...context,
      sessionId: context.sessionId ?? apSessionId(),
      actionAttemptId: uuid(),
      operationId: uuid(),
      inputMethod,
      startedAt: Date.now(),
      intents
    }
  },
  // Emit an allowlisted event. `key` deduplicates per screen visit.
  emit: (
    event: string,
    properties: Properties = {},
    context: ApContext | ApAttempt | undefined,
    key?: string
  ) => {
    const spec = registry.get(event)
    if (!spec || !context || !screens.has(context.screenId)) return
    const dedupKey = key && `${context.screenVisitId}:${event}:${key}`
    if (dedupKey && dedup.has(dedupKey)) return
    try {
      const eventId = uuid()
      const values = {
        ...context,
        ...('intents' in context ? context.intents : undefined),
        ...properties
      }
      // Context is built by our code, not user input.
      const payload = {
        ...contextFields(context),
        ...safeApProperties(
          [...spec.fields, ...attemptFields],
          values,
          spec.enums
        ),
        $session_id: apSessionId(),
        eventId,
        $insert_id: eventId,
        schemaVersion: 1,
        timestamp: new Date().toISOString()
      }
      posthogManager.captureWhenReady(client => {
        try {
          client.capture(event, {
            ...payload,
            $session_id: payload.$session_id ?? client.get_session_id()
          })
        } catch {
          /* Optional analytics. */
        }
      })
      if (dedupKey) {
        if (dedup.size > 2000) dedup.clear()
        dedup.add(dedupKey)
      }
    } catch {
      /* Analytics must not affect product actions. */
    }
  },
  // One errorId per error object, so every observation of it shares the ID.
  errorIds: (error: unknown): { errorId: string; errorCode: ApErrorCode } => {
    const existing =
      error && typeof error === 'object' ? errors.get(error) : undefined
    const result = {
      errorId: existing?.errorId ?? uuid(),
      errorCode: (existing?.errorCode ?? apErrorCode(error)) as ApErrorCode
    }
    if (error && typeof error === 'object') errors.set(error, result)
    return result
  }
}

// Extensions read by svc-partner-gateway (`extensions.apBills`). Only opaque
// IDs; identity comes from the authenticated session.
export const apRequestMetadata = (
  attempt: ApAttempt | undefined
): Record<string, string> => {
  if (
    !attempt ||
    !isApId(attempt.screenVisitId) ||
    !(apForwardedScreens as readonly string[]).includes(attempt.screenId)
  )
    return {}
  const metadata: Record<string, string> = {
    originScreenId: attempt.screenId,
    originScreenVisitId: attempt.screenVisitId
  }
  if (attempt.trafficClass !== 'partner_self_service')
    metadata.trafficClass = attempt.trafficClass
  const sessionId = attempt.sessionId ?? apSessionId()
  if (isApId(sessionId)) metadata.sessionId = sessionId
  if (isApId(attempt.actionAttemptId) && isApId(attempt.operationId)) {
    metadata.actionAttemptId = attempt.actionAttemptId
    metadata.operationId = attempt.operationId
  }
  for (const field of apIntentFields) {
    const value = attempt.intents?.[field]
    if (isApId(value)) metadata[field] = value
  }
  return metadata
}

const storable = (value: unknown): boolean =>
  isApId(value) ||
  typeof value === 'boolean' ||
  (typeof value === 'number' && Number.isFinite(value)) ||
  (typeof value === 'string' && /^[a-z][a-z0-9_]{0,60}$/.test(value)) ||
  (Array.isArray(value) && value.every(isApId))

const handoffPrefix = 'ap-handoff-v1:'
// Carry an attempt across navigation so the destination screen can report
// when it became usable. Missing observations stay unknown.
export const rememberApHandoff = (
  key: string,
  attempt: ApAttempt | undefined,
  properties: Properties = {}
) => {
  if (!attempt) return
  try {
    sessionStorage.setItem(
      handoffPrefix + key,
      JSON.stringify({
        attempt,
        properties: Object.fromEntries(
          Object.entries(properties).filter(([, value]) => storable(value))
        )
      })
    )
  } catch {
    /* Navigation still works. */
  }
}
export interface ApHandoff {
  attempt: ApAttempt
  properties: Properties
  elapsedMs: number
}
export const takeApHandoff = (
  key: string,
  accountId: string,
  match: (properties: Properties) => boolean = () => true
): ApHandoff | undefined => {
  try {
    const stored = sessionStorage.getItem(handoffPrefix + key)
    if (!stored) return undefined
    const handoff = JSON.parse(stored) as {
      attempt: ApAttempt
      properties: Properties
    }
    const elapsedMs = Date.now() - handoff.attempt.startedAt
    if (
      handoff.attempt.accountId !== accountId ||
      elapsedMs > 30 * 60 * 1000 ||
      !match(handoff.properties)
    )
      return undefined
    sessionStorage.removeItem(handoffPrefix + key)
    return { ...handoff, elapsedMs }
  } catch {
    return undefined
  }
}

const visitPrefix = 'ap-visit-v1:'
// Record a genuine screen entry and report the prior one in this browser
// session, for `returned`/`isReturnVisit` properties.
export const recordApVisit = (
  context: ApContext,
  entityId?: string
): { priorScreenVisitId?: string; elapsedSincePriorVisitMs?: number } => {
  const key = `${visitPrefix}${context.screenId}:${context.accountId}:${
    entityId ?? ''
  }`
  try {
    const prior = sessionStorage.getItem(key)
    sessionStorage.setItem(
      key,
      JSON.stringify({ screenVisitId: context.screenVisitId, at: Date.now() })
    )
    if (!prior) return {}
    const parsed = JSON.parse(prior) as { screenVisitId: string; at: number }
    if (parsed.screenVisitId === context.screenVisitId) return {}
    return {
      priorScreenVisitId: parsed.screenVisitId,
      elapsedSincePriorVisitMs: Math.max(0, Date.now() - parsed.at)
    }
  } catch {
    return {}
  }
}

// Coarse, value-free buckets for text lengths.
export const apLengthBucket = (value: string | null | undefined) => {
  const length = value?.length ?? 0
  return length === 0
    ? 'empty'
    : length <= 10
    ? '1_to_10'
    : length <= 30
    ? '11_to_30'
    : 'over_30'
}
